/**
 * F-073 — Post-game score submission dialog.
 *
 * Shows on game-over (loss or win) when the player has just set a new
 * personal best or forged a star. Prompts:
 *   - Signed in:   "Submit X points to global leaderboard?" → CTA submits.
 *   - Signed out:  "Sign in with Google to register your score" → CTA signs in.
 *   - Offline:     hidden — the dialog is leaderboard-only.
 *
 * Emits results via `onResult(success, rank)` so the caller can show
 * the leaderboard page after a successful submit.
 *
 * F-099 — pending submission 을 localStorage 에 임시 저장. 사용자가 로그인
 * 버튼을 누르면 OAuth 가 페이지를 리로드시켜 in-memory pending 이 휘발됨
 * → 점수 분실. signin 직전에 persist, OAuth 콜백 후 user 가 활성화되는
 * 시점에 자동 복원 + 다이얼로그 재오픈. 10분 TTL 로 stale 자동 만료.
 */

import { formatNumber, t } from "../i18n/index.ts";
import type { AuthController } from "../auth/auth-controller.ts";
import type { OnlineLeaderboardStore, SubmitInput } from "../leaderboard/online-store.ts";

const PENDING_KEY = "cosmic-suika.pending-submit";
const PENDING_TTL_MS = 10 * 60 * 1000;

interface PersistedPending {
  input: SubmitInput;
  ts: number;
}

function persistPending(input: SubmitInput): void {
  if (typeof localStorage === "undefined") return;
  try {
    const payload: PersistedPending = { input, ts: Date.now() };
    localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
  } catch {
    // quota / private mode — pending 분실. 최선 효과만.
  }
}

function loadFreshPending(): SubmitInput | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedPending;
    if (!parsed?.input || typeof parsed.ts !== "number") {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    if (Date.now() - parsed.ts > PENDING_TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return parsed.input;
  } catch {
    return null;
  }
}

function clearPending(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export interface ScoreSubmitDialog {
  element: HTMLDivElement;
  prompt(input: SubmitInput): void;
  hide(): void;
  detach(): void;
}

export interface ScoreSubmitDialogOptions {
  authController: AuthController;
  store: OnlineLeaderboardStore | null;
  onSubmitted: (rank: number | null) => void;
  onError?: (err: Error) => void;
}

export function createScoreSubmitDialog(
  host: HTMLElement,
  opts: ScoreSubmitDialogOptions,
): ScoreSubmitDialog {
  const element = document.createElement("div");
  element.dataset["testid"] = "score-submit-dialog";
  element.className =
    "pointer-events-auto absolute inset-x-0 bottom-24 mx-auto w-[min(420px,92vw)] flex-col gap-3 rounded-2xl bg-black/85 p-4 text-slate-100 shadow-2xl ring-1 ring-cosmic-glow/35 backdrop-blur-md sm:bottom-28";
  element.style.display = "none";

  const headline = document.createElement("div");
  headline.dataset["testid"] = "submit-headline";
  headline.className = "font-display text-base text-cosmic-star sm:text-lg";

  const detail = document.createElement("div");
  detail.dataset["testid"] = "submit-detail";
  detail.className = "text-xs text-slate-300";

  const actions = document.createElement("div");
  actions.className = "flex justify-end gap-2 pt-1";

  const dismiss = document.createElement("button");
  dismiss.type = "button";
  dismiss.dataset["testid"] = "submit-dismiss";
  dismiss.className =
    "rounded-md px-3 py-1 text-xs text-slate-300 hover:bg-white/10";

  const cta = document.createElement("button");
  cta.type = "button";
  cta.dataset["testid"] = "submit-cta";
  cta.className =
    "rounded-md bg-cosmic-glow/85 px-4 py-1 font-display text-xs font-bold text-cosmic-deep transition hover:bg-cosmic-glow disabled:cursor-not-allowed disabled:opacity-60";

  actions.append(dismiss, cta);
  element.append(headline, detail, actions);
  host.appendChild(element);

  let pending: SubmitInput | null = null;
  let busy = false;

  const hide = (): void => {
    element.style.display = "none";
    pending = null;
    busy = false;
    cta.disabled = false;
  };

  const renderState = (): void => {
    if (!pending) return;
    const user = opts.authController.getUser();
    headline.textContent = pending.starForged
      ? t("submit.headlineStar", { value: formatNumber(pending.score) })
      : t("submit.headline", { value: formatNumber(pending.score) });
    if (!opts.store) {
      detail.textContent = t("submit.offline");
      cta.style.display = "none";
      dismiss.textContent = t("submit.close");
      return;
    }
    cta.style.display = "";
    dismiss.textContent = t("submit.dismiss");
    if (user) {
      detail.textContent = t("submit.signedIn", { name: user.displayName });
      cta.textContent = t("submit.cta");
    } else {
      detail.textContent = t("submit.signedOut");
      cta.textContent = t("submit.ctaSignIn");
    }
  };

  const handleCta = async (): Promise<void> => {
    if (!pending || busy) return;
    const user = opts.authController.getUser();
    if (!user) {
      // F-099 — OAuth 가 페이지를 리로드시키므로 pending 을 localStorage
      // 에 백업. callback 후 user 가 잡히면 subscribe 핸들러가 복원.
      persistPending(pending);
      try {
        await opts.authController.signInWithGoogle(window.location.href);
      } catch (err) {
        opts.onError?.(err as Error);
      }
      return;
    }
    if (!opts.store) return;
    busy = true;
    cta.disabled = true;
    cta.textContent = t("submit.submitting");
    try {
      const result = await opts.store.submit({ ...pending, userId: user.id });
      clearPending(); // 성공 — 백업 정리
      opts.onSubmitted(result.rank);
      hide();
    } catch (err) {
      busy = false;
      cta.disabled = false;
      cta.textContent = t("submit.cta");
      opts.onError?.(err as Error);
      // Surface the actual reason so users (and us during debugging) can
      // see *why* it failed — not just a generic "submission failed".
      const message = err instanceof Error ? err.message : String(err);
      detail.textContent = `${t("submit.error")} (${message})`;
    }
  };

  cta.addEventListener("click", () => {
    void handleCta();
  });

  dismiss.addEventListener("click", () => {
    // F-099 — 사용자가 명시적으로 닫으면 백업도 정리. OAuth 안 거치고
    // 그냥 끄는 케이스.
    clearPending();
    hide();
  });

  // Re-render whenever auth state flips while dialog is open (sign-in
  // completes after redirect). F-099 — OAuth 콜백 후 user 가 활성화되면
  // localStorage 에 백업된 pending 을 자동 복원해 다이얼로그 재오픈.
  const unsub = opts.authController.subscribe((user) => {
    if (pending) {
      renderState();
      return;
    }
    if (user) {
      const restored = loadFreshPending();
      if (restored) {
        pending = restored;
        busy = false;
        cta.disabled = false;
        element.style.display = "flex";
        renderState();
      }
    }
  });

  return {
    element,
    prompt: (input) => {
      pending = input;
      busy = false;
      cta.disabled = false;
      element.style.display = "flex";
      renderState();
    },
    hide,
    detach: () => {
      unsub();
      element.remove();
    },
  };
}
